import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Table, Thead, Tbody, Tr, Th, Td, Switch, Box, Flex, Text, Spinner, IconButton, Input, InputGroup, InputLeftElement, InputRightElement, useToast } from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { FaEdit, FaSave } from "react-icons/fa";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Keyed on articlecode, NOT on the row's position. The rendered list is
  // filtered by the search box, so an index would point at a different article
  // than the one the user clicked edit on — and handleSave would write the
  // edited values to that other article.
  const [editRowCode, setEditRowCode] = useState(null);
  const [editRowData, setEditRowData] = useState({ brand: "", articleunitcost: "", articledescription: "" });
  const { selectedRetailer, selectedSupplier } = useSupplierRetailer();
  const toast = useToast();

  // Org admins use the org-scoped endpoints (supplier/retailer validated
  // server-side) and may edit description + cost + active only — never brand.
  const isOrgAdmin = localStorage.getItem("user_type") === "org_admin";
  const BASE = isOrgAdmin ? "org-admin" : "admin";

  useEffect(() => {
    if (selectedSupplier && selectedRetailer) {
      fetchArticles();
    }
  }, [selectedSupplier, selectedRetailer]);

  // Leaving a half-finished edit open across a supplier switch would show one
  // article's values against another's row.
  useEffect(() => {
    setEditRowCode(null);
    setSearch("");
  }, [selectedSupplier, selectedRetailer]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/${BASE}/get-articles`, {
        suppliercode: selectedSupplier,
        retailercode: selectedRetailer,
      });
      setArticles(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching articles:", error);
      setLoading(false);
    }
  };

  // The API now returns these ordered by description, but sorting here too means
  // the page reads alphabetically even against a backend that has not been
  // updated yet — the two deploy separately.
  const sortedArticles = useMemo(() => {
    return [...articles].sort((a, b) => {
      const byDescription = String(a.articledescription ?? "").localeCompare(String(b.articledescription ?? ""), undefined, { sensitivity: "base" });
      if (byDescription !== 0) return byDescription;
      // Repackaged sizes share a description; the code keeps their order stable.
      return String(a.articlecode ?? "").localeCompare(String(b.articlecode ?? ""));
    });
  }, [articles]);

  const visibleArticles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedArticles;
    return sortedArticles.filter(
      (article) =>
        String(article.articlecode ?? "").toLowerCase().includes(q) ||
        String(article.articledescription ?? "").toLowerCase().includes(q)
    );
  }, [sortedArticles, search]);

  const toggleActiveYn = async (articlecode, activeyn) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/${BASE}/toggle-article`, {
        suppliercode: selectedSupplier,
        retailercode: selectedRetailer,
        articlecode,
        activeyn: !activeyn,
      });
      setArticles((prevArticles) => prevArticles.map((article) => (article.articlecode === articlecode ? { ...article, activeyn: !activeyn } : article)));
    } catch (error) {
      console.error("Error updating article status:", error);
    }
  };

  const handleEdit = (article) => {
    setEditRowCode(article.articlecode);
    setEditRowData({
      brand: article.brand,
      articleunitcost: article.articleunitcost,
      articledescription: article.articledescription,
    });
  };

  const handleSave = async () => {
    const { brand, articleunitcost, articledescription } = editRowData;
    const articlecode = editRowCode;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/${BASE}/upd-article`, {
        brand,
        articleunitcost,
        articledescription,
        suppliercode: selectedSupplier,
        retailercode: selectedRetailer,
        articlecode,
      });
      toast({
        title: "Article updated",
        description: "The article has been updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setEditRowCode(null);
      fetchArticles();
    } catch (error) {
      toast({
        title: "Failed to update article",
        description: "An error occurred while updating the article.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box mt={16} maxWidth="97vw">
      {/* Filters as you type — the list is already in memory, so there is nothing
          to wait for and no reason to make the user press Enter. */}
      <Flex mb={3} align="center" gap={3} wrap="wrap">
        <InputGroup size="sm" width="320px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search article code or description"
            aria-label="Search articles"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearch("");
            }}
            borderColor="gray.300"
            borderRadius="md"
          />
          {search && (
            <InputRightElement>
              <IconButton aria-label="Clear search" icon={<CloseIcon boxSize={2.5} />} size="xs" variant="ghost" onClick={() => setSearch("")} />
            </InputRightElement>
          )}
        </InputGroup>
        <Text fontSize="sm" color="gray.500">
          {search ? `${visibleArticles.length} of ${articles.length} articles` : `${articles.length} articles`}
        </Text>
      </Flex>

      <Box overflowX="auto" maxHeight="900px" overflowY="auto">
        <Table variant="striped" size="sm" colorScheme="custom" border="1px" borderColor="gray.200" borderRadius="md">
          <Thead>
            <Tr>
              <Th>Article Code</Th>
              <Th>Description</Th>
              <Th>Unit Cost</Th>
              <Th>Brand</Th>
              <Th>Created Date</Th>
              <Th>Active</Th>
              <Th>Edit</Th>
            </Tr>
          </Thead>
          <Tbody>
            {visibleArticles.map((article) => {
              const isEditing = editRowCode === article.articlecode;
              return (
                <Tr key={article.articlecode}>
                  <Td>{article.articlecode}</Td>
                  <Td>{isEditing ? <Input value={editRowData.articledescription} onChange={(e) => setEditRowData({ ...editRowData, articledescription: e.target.value })} /> : article.articledescription}</Td>
                  <Td>{isEditing ? <Input value={editRowData.articleunitcost} onChange={(e) => setEditRowData({ ...editRowData, articleunitcost: e.target.value })} /> : article.articleunitcost}</Td>
                  <Td>{isEditing && !isOrgAdmin ? <Input value={editRowData.brand} onChange={(e) => setEditRowData({ ...editRowData, brand: e.target.value })} /> : article.brand}</Td>
                  <Td>{new Date(article.createddate).toLocaleDateString()}</Td>
                  <Td>
                    <Switch isChecked={article.activeyn} onChange={() => toggleActiveYn(article.articlecode, article.activeyn)} />
                  </Td>
                  <Td>{isEditing ? <IconButton aria-label="Save article" icon={<FaSave />} onClick={handleSave} colorScheme="green" variant="ghost" /> : <IconButton aria-label="Edit article" icon={<FaEdit />} onClick={() => handleEdit(article)} colorScheme="blue" variant="ghost" />}</Td>
                </Tr>
              );
            })}
            {visibleArticles.length === 0 && (
              <Tr>
                <Td colSpan={7}>
                  <Text fontSize="sm" color="gray.500" py={3}>
                    No articles match “{search}”.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default Articles;
